import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainTemplate from "../../../templates/MainTemplate";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Tabs,
  Tab,
  InputBase,
  IconButton,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Pagination,
  PaginationItem,
  CircularProgress,
  Tooltip,
  Snackbar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import TableViewIcon from "@mui/icons-material/TableView";
import GridViewIcon from "@mui/icons-material/GridView";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShareIcon from "@mui/icons-material/Share";
import PrintIcon from "@mui/icons-material/Print";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import SaveIcon from "@mui/icons-material/Save";

import { getList, deletePrebid } from "./service";
import { postUpdateAdminPrebid } from "../creation/service";
import { Bucket } from "../../../../api/config/apiConfig";
import CarMediaUploaderViewer from "../../../molecules/CarMediaUploaderViewer";
import ShareWithClientsModal from "../../../molecules/ShareWithClients";
import { sharePreviewLink } from "../creation/service";
import moment from "moment";

const ListingPrebid = () => {
  const navigate = useNavigate();

  // Navigation tab: 0 = Car Prebids & Agreements, 1 = Car Media Uploader & Viewer
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'

  // Prebid List State
  const [listOfPrebid, setListOfPrebid] = useState([]);
  const [totalRecord, setTotalRecords] = useState(0);
  const [whichPage, setWhichPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  // Selected prebid for standalone media tab
  const [selectedPrebidForMedia, setSelectedPrebidForMedia] = useState(null);

  // Quick Add Car Modal State
  const [isAddCarModalOpen, setIsAddCarModalOpen] = useState(false);
  const [isSubmittingCar, setIsSubmittingCar] = useState(false);
  const [newCarForm, setNewCarForm] = useState({
    make: "",
    model: "",
    year: moment().format("YYYY"),
    grade: "",
    specification: "",
    mileage: "",
    contractPrice: "",
    clientName: "",
    clientEmail: "",
    images: [],
  });

  // Modals & Snackbar
  const [deleteDialogId, setDeleteDialogId] = useState(null);
  const [shareDialogUuid, setShareDialogUuid] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const notify = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const getPrebidList = async () => {
    setLoading(true);
    getList(whichPage, itemsPerPage, search)
      .then((response) => {
        const list = response?.data?.data?.prebidList || [];
        setListOfPrebid(list);
        setTotalRecords(response?.data?.data?.totalRecord || list.length);
        if (list.length > 0 && !selectedPrebidForMedia) {
          setSelectedPrebidForMedia(list[0]);
        }
        setLoading(false);
      })
      .catch((_) => {
        setListOfPrebid([]);
        setTotalRecords(0);
        setLoading(false);
      });
  };

  useEffect(() => {
    getPrebidList();
  }, [itemsPerPage, search, whichPage]);

  const handleDelete = async (uuid) => {
    setDeleteLoadingId(uuid);
    deletePrebid(uuid)
      .then(() => {
        notify("Prebid agreement deleted successfully", "success");
        setDeleteDialogId(null);
        setDeleteLoadingId(null);
        getPrebidList();
      })
      .catch((err) => {
        notify(
          err?.response?.data?.message || "Failed to delete prebid",
          "error"
        );
        setDeleteLoadingId(null);
      });
  };

  const handleShareWithClients = (emails) => {
    if (!shareDialogUuid) return;
    const body = {
      uuid: shareDialogUuid,
      emails: emails.filter((e) => e.trim().length > 0),
    };
    sharePreviewLink(body)
      .then(() => {
        notify("Prebid preview link shared successfully with client(s)!");
        setShareDialogUuid(null);
      })
      .catch((err) => {
        notify(err?.response?.data?.message || "Failed to share preview", "error");
      });
  };

  // Sync photos updated from the Car Media tab to the backend prebid record
  const handleMediaImagesChange = (newImages) => {
    if (!selectedPrebidForMedia) return;

    const updatedPrebid = {
      ...selectedPrebidForMedia,
      carDescription: {
        ...selectedPrebidForMedia.carDescription,
        images: newImages,
      },
    };

    setSelectedPrebidForMedia(updatedPrebid);

    // Persist to backend
    postUpdateAdminPrebid(selectedPrebidForMedia.uuid, updatedPrebid)
      .then(() => {
        notify("Vehicle photos updated & saved successfully!", "success");
        // Update local list
        setListOfPrebid((prev) =>
          prev.map((item) =>
            item.uuid === selectedPrebidForMedia.uuid ? updatedPrebid : item
          )
        );
      })
      .catch((err) => {
        notify(err?.response?.data?.message || "Failed to save photos", "error");
      });
  };

  // Handle Quick Add Car & Upload Data Submit
  const handleQuickAddCarSubmit = () => {
    if (!newCarForm.make && !newCarForm.model) {
      notify("Please enter at least the vehicle Make or Model", "error");
      return;
    }

    setIsSubmittingCar(true);

    const payload = {
      issueDate: moment().format("YYYY-MM-DD"),
      date: moment().format("YYYY-MM-DD"),
      contractPrice: newCarForm.contractPrice || "",
      notice: "Confidentiality notice for car prebid import.",
      importantNote: "Vehicle condition is documented as per auction & inspection photos.",
      instructions:
        "1. Passport Copy: A clear and legible copy of the passport.\r2. Exemption Certificate from Ministry of Foreign Affairs.\r3. Copy of Diplomatic ID.\r4. Authority Letter for collecting and clearing consignment.\r5. Specimen Signature.",
      inclusions:
        "• Purchase of Vehicle in Japan Auction\r• Export documentation & handling\r• Sea Freight to Karachi Port\r• Destination Terminal Handling Charges (DTHC)\r• Wharfage & DO Charges\r• Transportation to Islamabad.",
      exclusions: "Demurrage, port storage, and detention charges if applicable.",
      demurrage: "Demurrage refers to official storage charges beyond allowed free time.",
      paymentTerms:
        "1. 50% Payment at the Time of Bill of Lading\r2. 50% Payment at the Time of Delivery.",
      carDescription: {
        make: newCarForm.make,
        model: newCarForm.model,
        year: newCarForm.year,
        grade: newCarForm.grade,
        specification: newCarForm.specification,
        mileage: newCarForm.mileage,
        images: newCarForm.images || [],
      },
      firstParty: {
        name: newCarForm.clientName || "Client / Buyer",
        email: newCarForm.clientEmail || "",
        mofaIdNo: "",
        sign: "",
        organization: "",
      },
      secondParty: {
        name: "Shelozon Logistics",
        email: "info@shelozon.com",
        mofaIdNo: "",
        sign: "",
        organization: "Shelozon Movers Packers & Logistics Pvt Ltd",
      },
    };

    postUpdateAdminPrebid("uuid", payload)
      .then((res) => {
        notify("Car & uploaded media created successfully!", "success");
        setIsSubmittingCar(false);
        setIsAddCarModalOpen(false);
        // Reset form
        setNewCarForm({
          make: "",
          model: "",
          year: moment().format("YYYY"),
          grade: "",
          specification: "",
          mileage: "",
          contractPrice: "",
          clientName: "",
          clientEmail: "",
          images: [],
        });
        getPrebidList();
      })
      .catch((err) => {
        console.error("Error creating car record:", err);
        notify(err?.response?.data?.message || "Failed to create car record", "error");
        setIsSubmittingCar(false);
      });
  };

  // Calculate high level metrics
  const totalPhotosCount = listOfPrebid.reduce(
    (acc, cur) => acc + (cur?.carDescription?.images?.length || 0),
    0
  );
  const totalMakesCount = new Set(
    listOfPrebid
      .map((p) => p?.carDescription?.make)
      .filter((m) => m && m.trim().length > 0)
  ).size;

  return (
    <MainTemplate>
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", backgroundColor: "#f8fafc" }}>
        {/* Top Header Banner */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <DirectionsCarIcon sx={{ fontSize: 36, color: "#0284c7" }} />
              Cars & Prebid Hub
            </Typography>
            <Typography variant="body1" sx={{ color: "#64748b", mt: 0.5 }}>
              Create vehicle records, manage prebid agreements, and upload/view car photos in PNG or JPG.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {/* Quick Upload Car Photos & Data Button */}
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setIsAddCarModalOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 2.5,
                py: 1.2,
                fontWeight: 700,
                fontSize: "0.9rem",
                color: "#0284c7",
                borderColor: "#0284c7",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 6px rgba(2, 132, 199, 0.1)",
                "&:hover": {
                  backgroundColor: "#f0f9ff",
                  borderColor: "#0369a1",
                },
              }}
            >
              Upload Car (PNG / JPG)
            </Button>

            {/* Create Full Prebid Agreement Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/create-update-prebid/uuid")}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                fontWeight: 700,
                fontSize: "0.9rem",
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                boxShadow: "0 4px 14px rgba(2, 132, 199, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0369a1 0%, #075985 100%)",
                },
              }}
            >
              Create Car Prebid
            </Button>
          </Box>
        </Box>

        {/* Metric Summary Cards */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#e0f2fe",
                  color: "#0284c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Total Prebids
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totalRecord || listOfPrebid.length}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#dcfce7",
                  color: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Active Makes
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totalMakesCount || (listOfPrebid.length > 0 ? "1+" : 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#fef3c7",
                  color: "#d97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Car Photos & Media
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totalPhotosCount} Photos
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#f3e8ff",
                  color: "#9333ea",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Supported Formats
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  PNG & JPG
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Section Navigation Tabs */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            p: 1,
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderRadius: "12px",
                minHeight: "44px",
                px: 3,
                mr: 1,
                color: "#64748b",
                "&.Mui-selected": {
                  color: "#0284c7",
                  backgroundColor: "#e0f2fe",
                },
              },
            }}
          >
            <Tab
              icon={<DirectionsCarIcon />}
              iconPosition="start"
              label="1. Car Prebids & Agreements"
            />
            <Tab
              icon={<PhotoLibraryIcon />}
              iconPosition="start"
              label="2. Car Media Uploader & Viewer"
            />
          </Tabs>
        </Paper>

        {/* TAB 1: CAR PREBIDS & AGREEMENTS LIST */}
        {activeTab === 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              p: { xs: 2, md: 3 },
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)",
            }}
          >
            {/* Search & View Controls Bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              {/* Search input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  px: 2,
                  py: 0.5,
                  width: { xs: "100%", sm: "360px" },
                }}
              >
                <SearchIcon sx={{ color: "#94a3b8", mr: 1 }} />
                <InputBase
                  placeholder="Search by car make, model, client..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setWhichPage(1);
                  }}
                  sx={{ width: "100%", fontSize: "0.9rem" }}
                />
              </Box>

              {/* View Toggle */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Table View">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("table")}
                    sx={{
                      backgroundColor: viewMode === "table" ? "#e0f2fe" : "#f8fafc",
                      color: viewMode === "table" ? "#0284c7" : "#64748b",
                      borderRadius: "8px",
                      p: 1,
                    }}
                  >
                    <TableViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Grid View">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("grid")}
                    sx={{
                      backgroundColor: viewMode === "grid" ? "#e0f2fe" : "#f8fafc",
                      color: viewMode === "grid" ? "#0284c7" : "#64748b",
                      borderRadius: "8px",
                      p: 1,
                    }}
                  >
                    <GridViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* TABLE VIEW */}
            {viewMode === "table" ? (
              <TableContainer sx={{ borderRadius: "12px", border: "1px solid #edf2f7" }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Vehicle / Car</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Issue Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Contract Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Client (1st Party)</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Media Files</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Signatures</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#475569" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={36} sx={{ color: "#0284c7" }} />
                          <Typography variant="body2" sx={{ color: "#64748b", mt: 1 }}>
                            Loading vehicle prebids...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : listOfPrebid?.length > 0 ? (
                      listOfPrebid.map((row, index) => {
                        const hasFirstSign = Boolean(row?.firstParty?.sign);
                        const hasSecondSign = Boolean(row?.secondParty?.sign);
                        const carImgCount = row?.carDescription?.images?.length || 0;
                        const coverImg = (row?.carDescription?.images || []).find(
                          (im) => im && typeof im === "object" && im.isCover
                        );
                        const firstImg = coverImg || row?.carDescription?.images?.[0];
                        const firstKey =
                          typeof firstImg === "string"
                            ? firstImg
                            : firstImg?.key || firstImg?.url;
                        const thumbUrl = firstKey
                          ? firstKey.startsWith("http")
                            ? firstKey
                            : `${Bucket}${firstKey}`
                          : null;

                        return (
                          <TableRow
                            key={row.uuid || index}
                            hover
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#f8fafc" },
                            }}
                          >
                            {/* Vehicle Column */}
                            <TableCell onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Avatar
                                  src={thumbUrl}
                                  variant="rounded"
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    bgcolor: "#e0f2fe",
                                    color: "#0284c7",
                                    borderRadius: "10px",
                                  }}
                                >
                                  <DirectionsCarIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                    {row?.carDescription?.make || "Vehicle"}{" "}
                                    {row?.carDescription?.model || ""}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                                    {row?.carDescription?.year || "Year N/A"} •{" "}
                                    {row?.carDescription?.mileage || "Mileage N/A"}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Issue Date */}
                            <TableCell onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}>
                              <Typography variant="body2" sx={{ color: "#334155" }}>
                                {row?.issueDate || "-"}
                              </Typography>
                            </TableCell>

                            {/* Contract Price */}
                            <TableCell onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}>
                              <Chip
                                label={row?.contractPrice || "Price N/A"}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: "#ecfdf5",
                                  color: "#059669",
                                  borderRadius: "6px",
                                }}
                              />
                            </TableCell>

                            {/* Client (1st Party) */}
                            <TableCell onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                                {row?.firstParty?.name || "Client Name N/A"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {row?.firstParty?.email || "-"}
                              </Typography>
                            </TableCell>

                            {/* Media Files Badge */}
                            <TableCell>
                              <Button
                                size="small"
                                startIcon={<PhotoLibraryIcon fontSize="small" />}
                                onClick={() => {
                                  setSelectedPrebidForMedia(row);
                                  setActiveTab(1);
                                }}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "8px",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: carImgCount > 0 ? "#0284c7" : "#94a3b8",
                                  bgcolor: carImgCount > 0 ? "#f0f9ff" : "#f1f5f9",
                                }}
                              >
                                {carImgCount} Photo{carImgCount === 1 ? "" : "s"}
                              </Button>
                            </TableCell>

                            {/* Signatures Status */}
                            <TableCell>
                              {hasFirstSign && hasSecondSign ? (
                                <Chip
                                  size="small"
                                  icon={<CheckCircleIcon fontSize="small" />}
                                  label="Signed (Both)"
                                  sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600 }}
                                />
                              ) : hasFirstSign || hasSecondSign ? (
                                <Chip
                                  size="small"
                                  icon={<PendingActionsIcon fontSize="small" />}
                                  label="Partially Signed"
                                  sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600 }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label="Unsigned"
                                  sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600 }}
                                />
                              )}
                            </TableCell>

                            {/* Action Buttons */}
                            <TableCell align="right">
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                                <Tooltip title="Edit Prebid">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}
                                    sx={{ color: "#0284c7" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Share with Client">
                                  <IconButton
                                    size="small"
                                    onClick={() => setShareDialogUuid(row.uuid)}
                                    sx={{ color: "#059669" }}
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete Prebid">
                                  <IconButton
                                    size="small"
                                    onClick={() => setDeleteDialogId(row.uuid)}
                                    disabled={deleteLoadingId === row.uuid}
                                    sx={{ color: "#dc2626" }}
                                  >
                                    {deleteLoadingId === row.uuid ? (
                                      <CircularProgress size={18} sx={{ color: "#dc2626" }} />
                                    ) : (
                                      <DeleteOutlineIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <DirectionsCarIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#475569" }}>
                            No Car Prebids Found
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2 }}>
                            {search
                              ? "No matching records found for your search query."
                              : "Click 'Upload Car (PNG / JPG)' or 'Create Car Prebid' above to add your first car."}
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => setIsAddCarModalOpen(true)}
                            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
                          >
                            Upload First Car & Photos
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              /* GRID VIEW */
              <Grid container spacing={3}>
                {listOfPrebid.map((row, index) => {
                  const carImgCount = row?.carDescription?.images?.length || 0;
                  const coverImg = (row?.carDescription?.images || []).find(
                    (im) => im && typeof im === "object" && im.isCover
                  );
                  const firstImg = coverImg || row?.carDescription?.images?.[0];
                  const firstKey =
                    typeof firstImg === "string"
                      ? firstImg
                      : firstImg?.key || firstImg?.url;
                  const thumbUrl = firstKey
                    ? firstKey.startsWith("http")
                      ? firstKey
                      : `${Bucket}${firstKey}`
                    : null;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={row.uuid || index}>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#ffffff",
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 12px 24px -4px rgba(15, 23, 42, 0.1)",
                          },
                        }}
                      >
                        {/* Vehicle Image Banner */}
                        <Box
                          sx={{
                            height: 160,
                            backgroundColor: "#f1f5f9",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt="Car"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <DirectionsCarIcon sx={{ fontSize: 64, color: "#cbd5e1" }} />
                          )}
                          <Chip
                            icon={<PhotoLibraryIcon fontSize="small" />}
                            label={`${carImgCount} Photos`}
                            size="small"
                            sx={{
                              position: "absolute",
                              bottom: 10,
                              right: 10,
                              backgroundColor: "rgba(15, 23, 42, 0.75)",
                              color: "#ffffff",
                              backdropFilter: "blur(4px)",
                              fontWeight: 600,
                            }}
                          />
                        </Box>

                        {/* Card Content */}
                        <Box sx={{ p: 2.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {row?.carDescription?.make || "Vehicle"} {row?.carDescription?.model || ""}
                            </Typography>
                            <Chip
                              label={row?.contractPrice || "Price N/A"}
                              size="small"
                              sx={{ fontWeight: 700, bgcolor: "#ecfdf5", color: "#059669" }}
                            />
                          </Box>

                          <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                            Client: <b>{row?.firstParty?.name || "N/A"}</b>
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                            <Chip
                              label={row?.carDescription?.year || "Year N/A"}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                            <Chip
                              label={row?.carDescription?.mileage || "Mileage N/A"}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                            <Chip
                              label={`Issue: ${row?.issueDate || "-"}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          </Box>

                          {/* Card Action Buttons */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "1px solid #f1f5f9",
                              pt: 1.5,
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => navigate(`/create-update-prebid/${row.uuid}`)}
                              sx={{ textTransform: "none", fontWeight: 700, color: "#0284c7" }}
                            >
                              Edit Prebid
                            </Button>

                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="View Media">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedPrebidForMedia(row);
                                    setActiveTab(1);
                                  }}
                                  sx={{ color: "#0284c7" }}
                                >
                                  <PhotoLibraryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Share">
                                <IconButton
                                  size="small"
                                  onClick={() => setShareDialogUuid(row.uuid)}
                                  sx={{ color: "#059669" }}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDialogId(row.uuid)}
                                  sx={{ color: "#dc2626" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Pagination Controls */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mt: 4,
                pt: 2,
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setWhichPage(1);
                    }}
                    sx={{ borderRadius: "10px", backgroundColor: "#ffffff" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Entries per page
                </Typography>
              </Box>

              <Pagination
                count={Math.ceil((totalRecord || listOfPrebid.length) / itemsPerPage) || 1}
                page={whichPage}
                onChange={(_, newPage) => setWhichPage(newPage)}
                color="primary"
                renderItem={(item) => (
                  <PaginationItem
                    component="button"
                    onClick={() => setWhichPage(item.page)}
                    {...item}
                  />
                )}
                prevIcon={<ArrowCircleLeftIcon />}
                nextIcon={<ArrowCircleRightIcon />}
              />
            </Box>
          </Paper>
        )}

        {/* TAB 2: CAR MEDIA UPLOADER & VIEWER */}
        {activeTab === 1 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              p: { xs: 2.5, md: 4 },
            }}
          >
            {/* Vehicle Selector bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 4,
                p: 2.5,
                borderRadius: "14px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569" }}>
                  Select Vehicle / Prebid:
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Manage and view car photos specifically attached to this vehicle agreement.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <Select
                    value={selectedPrebidForMedia?.uuid || ""}
                    onChange={(e) => {
                      const match = listOfPrebid.find((p) => p.uuid === e.target.value);
                      if (match) setSelectedPrebidForMedia(match);
                    }}
                    displayEmpty
                    sx={{ borderRadius: "10px", backgroundColor: "#ffffff" }}
                  >
                    {listOfPrebid.map((p) => (
                      <MenuItem key={p.uuid} value={p.uuid}>
                        🚗 {p?.carDescription?.make || "Car"} {p?.carDescription?.model || ""} - {p?.firstParty?.name || "Client"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddCarModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    color: "#0284c7",
                    borderColor: "#0284c7",
                  }}
                >
                  + New Car & Photos
                </Button>
              </Box>
            </Box>

            {/* Reusable Car Media Uploader & Lightbox Viewer */}
            <CarMediaUploaderViewer
              images={selectedPrebidForMedia?.carDescription?.images || []}
              onImagesChange={handleMediaImagesChange}
              isEdit={true}
              onNotify={notify}
              title={`Vehicle Gallery: ${selectedPrebidForMedia?.carDescription?.make || ""} ${selectedPrebidForMedia?.carDescription?.model || "Car"}`}
              subtitle="Upload PNG and JPG high-res car exterior, interior, engine bay photos, or inspection documents."
            />
          </Paper>
        )}

        {/* QUICK ADD CAR & UPLOAD MEDIA MODAL */}
        <Dialog
          open={isAddCarModalOpen}
          onClose={() => !isSubmittingCar && setIsAddCarModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              p: 1,
              maxHeight: "92vh",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DirectionsCarIcon sx={{ color: "#0284c7", fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Upload Car & Media Data (PNG / JPG)
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Add a new vehicle record and immediately upload its photos and documents.
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setIsAddCarModalOpen(false)} disabled={isSubmittingCar}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ py: 3 }}>
            {/* Car Specs Form */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
              1. Car Specifications
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Make / Brand *"
                  placeholder="e.g. Toyota, Honda, BMW"
                  value={newCarForm.make}
                  onChange={(e) => setNewCarForm({ ...newCarForm, make: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Model & Variant *"
                  placeholder="e.g. Land Cruiser Prado TX, Civic"
                  value={newCarForm.model}
                  onChange={(e) => setNewCarForm({ ...newCarForm, model: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manufacturing Year"
                  placeholder="e.g. 2023"
                  value={newCarForm.year}
                  onChange={(e) => setNewCarForm({ ...newCarForm, year: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Grade / Package"
                  placeholder="e.g. Package L, 4.5 Grade"
                  value={newCarForm.grade}
                  onChange={(e) => setNewCarForm({ ...newCarForm, grade: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mileage"
                  placeholder="e.g. 25,000 km"
                  value={newCarForm.mileage}
                  onChange={(e) => setNewCarForm({ ...newCarForm, mileage: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Specification / Features"
                  placeholder="e.g. 4WD, Sunroof, Leather Seats"
                  value={newCarForm.specification}
                  onChange={(e) => setNewCarForm({ ...newCarForm, specification: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contract / Estimated Price"
                  placeholder="e.g. $35,000 USD / ¥5,200,000"
                  value={newCarForm.contractPrice}
                  onChange={(e) => setNewCarForm({ ...newCarForm, contractPrice: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client / Buyer Name"
                  placeholder="e.g. John Doe"
                  value={newCarForm.clientName}
                  onChange={(e) => setNewCarForm({ ...newCarForm, clientName: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client Email"
                  placeholder="e.g. client@example.com"
                  value={newCarForm.clientEmail}
                  onChange={(e) => setNewCarForm({ ...newCarForm, clientEmail: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* PNG / JPG Media Uploader */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
              2. Upload Car Photos & Inspection Sheets (PNG / JPG)
            </Typography>

            <CarMediaUploaderViewer
              images={newCarForm.images || []}
              onImagesChange={(imgs) => setNewCarForm({ ...newCarForm, images: imgs })}
              isEdit={true}
              onNotify={notify}
              title="Car Photos Dropzone"
              subtitle="Drag & drop PNG or JPG images of this vehicle, auction sheets, or interior/exterior views."
            />
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setIsAddCarModalOpen(false)}
              disabled={isSubmittingCar}
              sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isSubmittingCar ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
              disabled={isSubmittingCar}
              onClick={handleQuickAddCarSubmit}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3,
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
              }}
            >
              {isSubmittingCar ? "Saving Car Data..." : "Save Car & Media"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={Boolean(deleteDialogId)}
          onClose={() => setDeleteDialogId(null)}
          PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#0f172a" }}>
            Delete Car Prebid Agreement?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Are you sure you want to permanently delete this car prebid agreement? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteDialogId(null)}
              sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteDialogId)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
            >
              Delete Agreement
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Preview Modal */}
        {Boolean(shareDialogUuid) && (
          <ShareWithClientsModal
            open={Boolean(shareDialogUuid)}
            onClose={() => setShareDialogUuid(null)}
            onSuccess={handleShareWithClients}
          />
        )}

        {/* Global Notifications Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={() => setOpenSnackbar(false)}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </MainTemplate>
  );
};

export default ListingPrebid;
